import { ReactNode } from 'react'
import { ProgressBar } from '@firstbite/ui'

interface WizardHeaderProps {
  eyebrow: string
  title: string
  /** 1-based current step. Omit for a single-step flow (no stepper). */
  stepNum?: number
  /** Total steps. Omit for a single-step flow (no stepper). */
  stepCount?: number
  /**
   * Force-hide the stepper even when step data is present (e.g. a pre-step
   * chooser). The stepper is otherwise shown only when both stepNum and
   * stepCount are provided.
   */
  showStepper?: boolean
  children?: ReactNode
}

/**
 * Shared header shell for the full-screen mobile wizards (create note, create
 * contact). Renders the progress row ("Step X of Y" + `ProgressBar`), the
 * uppercase eyebrow, and the step title. Anything step-specific below the title
 * (a subtitle line, company/door breadcrumb links) goes in `children`.
 *
 * Used with `MobileModal(fullScreen)` — pass the returned element as `header`.
 */
export function WizardHeader({
  eyebrow,
  title,
  stepNum,
  stepCount,
  showStepper = true,
  children,
}: WizardHeaderProps) {
  // Only render the stepper when we actually have step data (and it isn't
  // force-hidden), so single-step flows can omit stepNum/stepCount entirely.
  const hasStepper = showStepper && stepNum != null && stepCount != null
  const progressPct =
    stepNum != null && stepCount ? (stepNum / stepCount) * 100 : 0
  return (
    <div className="p-2">
      {hasStepper && (
        <div className="mb-3 flex items-center gap-3">
          <ProgressBar value={progressPct} className="flex-1" />
          <span className="shrink-0 whitespace-nowrap text-xs text-fg-tertiary">
            Step {stepNum} of {stepCount}
          </span>
        </div>
      )}
      <div className="text-xs uppercase tracking-wide text-fg-tertiary mb-1">
        {eyebrow}
      </div>
      <h1 className="text-2xl font-semibold text-fg">{title}</h1>
      {children}
    </div>
  )
}
